import * as appConfig from './app.config';
import * as moment from 'moment';

import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HttpClientModule } from '@angular/common/http';
import { NgModule } from '@angular/core';

import { UiSwitchModule } from 'ngx-ui-switch';
import { DragulaModule } from 'ng2-dragula';
import { TreeModule } from '@circlon/angular-tree-component';
import { ColorPickerModule, ColorPickerService } from 'ngx-color-picker';
import { NgxPaginationModule } from 'ngx-pagination';
import { DropzoneModule, DROPZONE_CONFIG } from 'ngx-dropzone-wrapper';
import { NgSelectModule } from '@ng-select/ng-select';
import { FaIconLibrary, FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { fab } from '@fortawesome/free-brands-svg-icons';

import { DpDatePickerModule } from 'ng2-date-picker';

import { AppRoutingModule } from './app-routing.module';

import { AppComponent } from './app.component';

import { ApiService } from './shared/services/api.service';
import { AlertService } from './shared/services/alert.service';
import { CommunicationService } from './shared/services/communication.service';
import { AuthenticationService } from './shared/services/authentication.service';
import { LocalstorageService } from './shared/services/localstorage.service';
import { FilesService } from './shared/services/files.service';

import { AuthGuard } from './shared/guards/auth.guard';

import { FilterListComponent } from './shared/components/filter-list/filter-list.component';
import { AlertComponent } from './shared/components/alert/alert.component';
import { FormEditorsComponent } from './shared/components/form-fields/form-fields.component';
import { FormGroupComponent } from './shared/components/form-fields/form-group.component';
import { FormEditorComponent } from './shared/components/form-fields/form-field.component';
import { ModalComponent } from './shared/components/modal/modal.component';
import { ConfirmationComponent } from './shared/components/modal/confirmation/confirmation.component';
import { NoDataComponent } from './shared/components/no-data/no-data.component';
import { DropzoneComponent } from './shared/components/dropzone/dropzone.component';

import { HeaderComponent } from './components/header/header.component';
import { HeaderBreadcrumbsComponent } from './components/header/header-breadcrumbs.component';
import { HeaderNotificationsComponent } from './components/header/header-notifications.component';

import { NavigationComponent } from './components/layout/navigation.component';

import { NoveltiesComponent } from './components/novelties/novelties.component';
import { MenuComponent } from './components/menu/menu.component';
import { NewsComponent } from './components/novelties/news.component';
import { NewsListComponent } from './components/novelties/news-list.component';
import { NewsDetailComponent } from './components/novelties/news-detail.component';

import { PagesComponent } from './components/pages/pages.component';
import { PageDetailComponent } from './components/pages/page-detail.component';
import { PageListComponent } from './components/pages/page-list.component';

import { SeoSettingsComponent } from './components/settings/seo-settings.component';
import { SettingsComponent } from './components/settings/settings.component';
import { AppearanceComponent } from './components/appearance/appearance.component';
import { LogoComponent } from './components/appearance/logo.component';

import { FilesComponent } from './components/files/files.component';
import { FileListComponent } from './components/files/file-list.component';
import { LoginComponent } from './components/user/login.component';
import { LayoutComponent } from './components/layout/layout.component';
import { FileZoomComponent } from './components/files/file-zoom.component';
import { FileDetailComponent } from './components/files/file-detail.component';
import { FileBrowseComponent } from './shared/components/browse/file-browse.component';
import { HomepageComponent } from './components/homepage/homepage.component';
import { SlidesComponent } from './components/homepage/slides.component';
import { SlideListComponent } from './components/homepage/slide-list.component';
import { SlideDetailComponent } from './components/homepage/slide-detail.component';

import { HomeTilesComponent } from './components/homepage/hometiles.component';
import { HomeTileListComponent } from './components/homepage/hometile-list.component';
import { HomeTileDetailComponent } from './components/homepage/hometile-detail.component';
import { FooterComponent } from './components/footer/footer.component';
import { CustomMenuItemDetailComponent } from './components/menu/custom-menu-item-detail.component';
import { MenuService } from './components/menu/services/menu.service';
import { PageTilesComponent } from './components/pages/page-tiles.component';
import { PageContentboxesComponent } from './components/pages/page-contentboxes.component';
import { TilesListComponent } from './components/pages/tiles-list.component';
import { ContentboxesListComponent } from './components/pages/contentboxes-list.component';
import { PageTileDetailComponent } from './components/pages/page-tile-detail.component';
import { EntityBrowseComponent } from './shared/components/browse/entity-browse.component';
import { EntityService } from './shared/services/entity.service';
import { NewsletterComponent } from './components/newsletter/newsletter.component';
import { SubscribersComponent } from './components/newsletter/subscribers.component';
import { SubscriberDetailComponent } from './components/newsletter/subscriber-detail.component';
import { CampaignsListComponent } from './components/newsletter/campaigns-list.component';
import { CampaignDetailComponent } from './components/newsletter/campaign-detail.component';
import { FormsComponent } from './components/forms/forms.component';
import { FormDetailComponent } from './components/forms/form-detail.component';
import { FormsListComponent } from './components/forms/forms-list.component';
import { UserComponent } from './components/user/user.component';
import { MceDropzoneComponent } from './shared/components/tinymce/mce-dropzone/mce-dropzone.component';
import { MceBrowseComponent } from './shared/components/tinymce/mce-browse/mce-browse.component';
import { FaviconComponent } from './components/appearance/favicon.component';
import { FaviconIcoComponent } from './components/appearance/favicon-ico.component';
import { FormRecordsComponent } from './components/forms/form-records.component';
import { SocialComponent } from './components/settings/social.component';
import { SocialListComponent } from './components/settings/social-list.component';
import { SocialDetailComponent } from './components/settings/social-detail.component';
import { ScriptsComponent } from './components/settings/scripts.component';
import { ArticlesComponent } from './components/novelties/articles.component';
import { CategoriesComponent } from './components/novelties/categories.component';
import { ArticleListComponent } from './components/novelties/article-list.component';
import { ArticleDetailComponent } from './components/novelties/article-detail.component';
import { CategoryListComponent } from './components/novelties/category-list.component';
import { CategoryDetailComponent } from './components/novelties/category-detail.component';
import { WysiwygComponent } from './shared/components/form-fields/editors/wysiwyg/wysiwyg.component';
import { GdprComponent } from './components/settings/gdpr.component';
import { PasswordComponent } from './components/user/password.component';
import { ProfileComponent } from './components/user/profile.component';
import { HomeBottomTextComponent } from './components/homepage/home-bottom-text.component';
import { ShowNewsOnHomepageComponent } from './components/novelties/show-news-on-homepage.component';
import {
  ShowArticlesOnHomepageComponent
} from './components/novelties/show-articles-on-homepage.component';
import { TextboxComponent } from './shared/components/form-fields/editors/textbox/textbox.component';
import { DropdownComponent } from './shared/components/form-fields/editors/dropdown/dropdown.component';
import { TextareaComponent } from './shared/components/form-fields/editors/textarea/textarea.component';
import { SwitchComponent } from './shared/components/form-fields/editors/switch/switch.component';
import { ContainedFieldsComponent } from './shared/components/form-fields/editors/contained-fields/contained-fields.component';
import { TagsComponent } from './shared/components/form-fields/editors/tags/tags.component';
import { ContainedComponent } from './shared/components/form-fields/editors/contained/contained.component';
import { ContainedManyComponent } from './shared/components/form-fields/editors/contained-many/contained-many.component';
import { TokenInterceptor } from './shared/interceptors/api.interceptor';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { ContentboxDetailComponent } from './components/pages/contentbox.detail.component';
import { RealEstateComponent } from './components/real-estate/real-estate.component';
import { RealEstateListComponent } from './components/real-estate/real-estate-list.component';
import { EstateAgentsListComponent } from './components/real-estate/estate-agents-list.component';
import { DeveloperProjectsListComponent } from './components/real-estate/developer-projects-list.component';
import { DeveloperProjectsComponent } from './components/real-estate/developer-projects.component';
import { EstateAgentsComponent } from './components/real-estate/estate-agents.component';
import { RealEstateDetailComponent } from './components/real-estate/real-estate-detail.component';
import { EstateAgentDetailComponent } from './components/real-estate/estate-agent-detail.component';
import { DeveloperProjectDetailComponent } from './components/real-estate/developer-project-detail.component';
import { EstateFormfieldsComponent } from './components/real-estate/estate-formfields.component';
import { EstateFormRecordsComponent } from './components/real-estate/estate-form-records.component';
import { AppointmentsComponent } from './components/appointments/appointments.component';
import { CalendarListComponent } from './components/appointments/calendar-list.component';
import { CalendarDetailComponent } from './components/appointments/calendar-detail.component';
import { AppointmentsOverviewComponent } from './components/appointments/appointments-overview.component';
import { AppointmentsCalendarComponent } from './components/appointments/appointments-calendar.component';
import { AppointmentsWindowComponent } from './components/appointments/appointments-window.component';
import { AppointmentHolidaysComponent } from './components/appointments/appointment-holidays.component';
import { AppointmentHolidaysListComponent } from './components/appointments/appointment-holidays-list.component';
import { AppointmentHolidayDetailComponent } from './components/appointments/appointment-holiday-detail.component';
import { DateTimePickerComponent } from './shared/components/form-fields/editors/datetimepicker/datetimepicker.component';
import { DatePickerEditorComponent } from './shared/components/form-fields/editors/datepicker/datepicker.component';
import { TimePickerComponent } from './shared/components/form-fields/editors/timepicker/timepicker.component';
import { BookingsComponent } from './components/bookings/bookings.component';
import { BookingOfferListComponent } from './components/bookings/booking-offer-list.component';
import { BookingItemsListComponent } from './components/bookings/booking-items-list.component';
import { BookingItemDetailComponent } from './components/bookings/booking-item-detail.component';
import { BookingOfferDetailComponent } from './components/bookings/booking-offer-detail.component';
import { BookingHolidayListComponent } from './components/bookings/booking-holiday-list.component';
import { BookingHolidayDetailComponent } from './components/bookings/booking-holiday-detail.component';
import { BookingsOverviewComponent } from './components/bookings/bookings-overview.component';
import { BookingsWindowComponent } from './components/bookings/bookings-window.component';
import { SvglistEditorComponent } from './shared/components/form-fields/editors/svglist/svglist-editor.component';
import { ParametersComponent } from './shared/components/form-fields/editors/parameters/parameters.component';
import { ContactsComponent } from './components/contacts/contacts.component';
import { DesignboxesListComponent } from './components/pages/designboxes-list.component';
import { DesignboxDetailComponent } from './components/pages/designbox.detail.component';
import { PageDesignboxesComponent } from './components/pages/page-designboxes.component';
import { StoryboardsListComponent } from './components/pages/storyboards-list.component';
import { StoryboardDetailComponent } from './components/pages/storyboard-detail.component';
import { PageStoryboardsComponent } from './components/pages/page-storyboards.component';
import { ProductListComponent } from './components/products/product-list.component';
import { ProductDetailComponent } from './components/products/product-detail.component';
import { ProductCategoryDetailComponent } from './components/products/product-category-detail.component';
import { ProductCategoryListComponent } from './components/products/product-category-list.component';
import { ProductsComponent } from './components/products/products.component';
import { TestimonialListComponent } from './components/products/testimonial-list.component';
import { TestimonialDetailComponent } from './components/products/testimonial-detail.component';
import { OfficeContactListComponent } from './components/contacts/office-contact-list.component';
import { OfficeContactsComponent } from './components/contacts/office-contacts.component';
import { OfficeContactDetailComponent } from './components/contacts/office-contact-detail.component';
import { OfficesComponent } from './components/contacts/offices.component';
import { OfficeListComponent } from './components/contacts/office-list.component';
import { OfficeDetailComponent } from './components/contacts/office-detail.component';
import { AboutComponent } from './components/contacts/about.component';
import { ContactsmapComponent } from './components/contacts/contacts-map.component';
import { JobListComponent } from './components/career/job-list.component';
import { JobDetailComponent } from './components/career/job-detail.component';
import { CareerComponent } from './components/career/career.component';
import { CareerDescComponent } from './components/career/career-desc.component';
import { GrantDetailComponent } from './components/certs/grant-detail.component';
import { GrantListComponent } from './components/certs/grant-list.component';
import { CertDetailComponent } from './components/certs/cert-detail.component';
import { CertListComponent } from './components/certs/cert-list.component';
import { CertsComponent } from './components/certs/certs.component';
import { FooterMenuComponent } from './components/footer/footer-menu.component';
import { FooterSettingsComponent } from './components/footer/footer-settings.component';
import { TestimonialSettingsComponent } from './components/products/testimonial-settings.component';
import { SponsoredDetailComponent } from './components/contacts/sponsored-detail.component';
import { SponsoredListComponent } from './components/contacts/sponsored-list.component';
import { SponsoredComponent } from './components/contacts/sponsored.component';
import { PartnerDetailComponent } from './components/contacts/partner-detail.component';
import { PartnersComponent } from './components/contacts/partners.component';
import { PartnerListComponent } from './components/contacts/partner-list.component';
import { EmailSignaturesComponent } from './components/settings/email-signatures.component';

@NgModule({
  declarations: [
    AppComponent,
    HeaderComponent,
    HeaderBreadcrumbsComponent,
    HeaderNotificationsComponent,
    NavigationComponent,
    NoveltiesComponent,
    PagesComponent,
    PageListComponent,
    MenuComponent,
    SeoSettingsComponent,
    FormEditorComponent,
    FormEditorsComponent,
    SettingsComponent,
    AppearanceComponent,
    FilesComponent,
    FileListComponent,
    PageDetailComponent,
    NewsComponent,
    NewsListComponent,
    NewsDetailComponent,
    LogoComponent,
    FilterListComponent,
    AlertComponent,
    ModalComponent,
    ConfirmationComponent,
    NoDataComponent,
    LoginComponent,
    LayoutComponent,
    DropzoneComponent,
    FileZoomComponent,
    FormGroupComponent,
    FileDetailComponent,
    FileBrowseComponent,
    HomepageComponent,
    SlidesComponent,
    SlideListComponent,
    SlideDetailComponent,
    HomeTilesComponent,
    HomeTileListComponent,
    HomeTileDetailComponent,
    FooterComponent,
    CustomMenuItemDetailComponent,
    PageTilesComponent,
    PageContentboxesComponent,
    TilesListComponent,
    ContentboxesListComponent,
    PageTileDetailComponent,
    ContentboxDetailComponent,
    EntityBrowseComponent,
    NewsletterComponent,
    SubscribersComponent,
    SubscriberDetailComponent,
    CampaignsListComponent,
    CampaignDetailComponent,
    FormsComponent,
    FormDetailComponent,
    FormsListComponent,
    UserComponent,
    MceDropzoneComponent,
    MceBrowseComponent,
    FaviconComponent,
    FaviconIcoComponent,
    FormRecordsComponent,
    SocialComponent,
    SocialListComponent,
    SocialDetailComponent,
    ScriptsComponent,
    ArticlesComponent,
    CategoriesComponent,
    ArticleListComponent,
    ArticleDetailComponent,
    CategoryListComponent,
    CategoryDetailComponent,
    WysiwygComponent,
    GdprComponent,
    PasswordComponent,
    ProfileComponent,
    HomeBottomTextComponent,
    ShowNewsOnHomepageComponent,
    ShowArticlesOnHomepageComponent,
    TextboxComponent,
    DropdownComponent,
    TextareaComponent,
    SwitchComponent,
    ContainedFieldsComponent,
    TagsComponent,
    ContainedComponent,
    ContainedManyComponent,
    SvglistEditorComponent,
    ParametersComponent,
    RealEstateComponent,
    RealEstateDetailComponent,
    RealEstateListComponent,
    EstateAgentsComponent,
    EstateAgentDetailComponent,
    EstateAgentsListComponent,
    DeveloperProjectsComponent,
    DeveloperProjectDetailComponent,
    DeveloperProjectsListComponent,
    EstateFormfieldsComponent,
    EstateFormRecordsComponent,
    AppointmentsComponent,
    CalendarListComponent,
    CalendarDetailComponent,
    AppointmentsOverviewComponent,
    AppointmentsCalendarComponent,
    AppointmentsWindowComponent,
    AppointmentHolidaysComponent,
    AppointmentHolidaysListComponent,
    AppointmentHolidayDetailComponent,
    DateTimePickerComponent,
    DatePickerEditorComponent,
    TimePickerComponent,
    BookingsComponent,
    BookingOfferListComponent,
    BookingItemsListComponent,
    BookingItemDetailComponent,
    BookingOfferDetailComponent,
    BookingHolidayListComponent,
    BookingHolidayDetailComponent,
    BookingsOverviewComponent,
    BookingsWindowComponent,
    ContactsComponent,
    DesignboxesListComponent,
    DesignboxDetailComponent,
    PageDesignboxesComponent,
    StoryboardsListComponent,
    StoryboardDetailComponent,
    PageStoryboardsComponent,
    ProductListComponent,
    ProductDetailComponent,
    ProductCategoryDetailComponent,
    ProductCategoryListComponent,
    ProductsComponent,
    TestimonialListComponent,
    TestimonialDetailComponent,
    OfficeContactListComponent,
    OfficeContactsComponent,
    OfficeContactDetailComponent,
    OfficesComponent,
    OfficeListComponent,
    OfficeDetailComponent,
    AboutComponent,
    ContactsmapComponent,
    CareerComponent,
    JobListComponent,
    JobDetailComponent,
    CareerDescComponent,
    GrantDetailComponent,
    GrantListComponent,
    CertDetailComponent,
    CertListComponent,
    CertsComponent,
    FooterSettingsComponent,
    FooterMenuComponent,
    TestimonialSettingsComponent,
    SponsoredDetailComponent,
    SponsoredListComponent,
    SponsoredComponent,
    PartnerDetailComponent,
    PartnersComponent,
    PartnerListComponent,
    EmailSignaturesComponent
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    FormsModule,
    ReactiveFormsModule,
    HttpClientModule,
    AppRoutingModule,
    DragulaModule.forRoot(),
    NgSelectModule,
    TreeModule,
    UiSwitchModule,
    ColorPickerModule,
    NgxPaginationModule,
    DropzoneModule,
    DpDatePickerModule,
    FontAwesomeModule
  ],
  providers: [
    ColorPickerService,
    ApiService,
    AlertService,
    CommunicationService,
    AuthGuard,
    AuthenticationService,
    LocalstorageService,
    FilesService,
    MenuService,
    EntityService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TokenInterceptor,
      multi: true
    },
    {
      provide: DROPZONE_CONFIG,
      useValue: appConfig.dropzoneConfig
    }],
  bootstrap: [AppComponent]
})
export class AppModule {
  constructor(library: FaIconLibrary) {
    moment.locale('cs');
    library.addIconPacks(fab);
  }
}
